import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  Ticket,
  Plus,
  RefreshCw,
} from "lucide-react";
import { Button, SearchField, EmptyState } from "@/components/ui";
import { PageFrame, ListResultsCard, ErrorState } from "@/components/common";
import { matchesTextQuery } from "@/lib/textSearch";
import { NEW_SUPPORT_TICKET_PATH } from "./supportPaths";
import { useAuthStore } from "@/store/authStore";
import { Permission } from "@/types";
import {
  TicketListItem,
  TicketStatsGrid,
  parseTicketListSearchParams,
  buildTicketListApiParams,
  getSelectedListView,
  type TicketStatus,
  type TicketListView,
} from "@/components/tickets";
import {
  supportTicketKeys,
  supportTicketService,
} from "@/services/supportTicketService";

export default function TicketListPage() {
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("support");
  const { hasPermission } = useAuthStore();
  const showPriority = hasPermission(Permission.SUPPORT_TICKETS_UPDATE_ANY);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchQuery, setSearchQuery] = useState("");

  const { statusFilter, view } = parseTicketListSearchParams(searchParams);
  const selectedView = getSelectedListView(statusFilter, view);
  const listParams = buildTicketListApiParams(statusFilter, view);

  const {
    data: ticketsData,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: supportTicketKeys.myList(listParams),
    queryFn: () => supportTicketService.getMyTickets(listParams),
    placeholderData: keepPreviousData,
  });

  const { data: stats } = useQuery({
    queryKey: supportTicketKeys.myStats(),
    queryFn: () => supportTicketService.getMyStats(),
  });

  const tickets = ticketsData?.items ?? [];

  const updateView = (nextView: TicketListView) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("status");
    if (nextView === "ongoing") {
      newParams.delete("view");
    } else {
      newParams.set("view", nextView);
    }
    setSearchParams(newParams);
  };

  const updateStatus = (status: TicketStatus | null) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("view");
    if (status) {
      newParams.set("status", status);
    } else {
      newParams.delete("status");
    }
    setSearchParams(newParams);
  };

  const filteredTickets = tickets.filter((ticket) => {
    if (!searchQuery) return true;
    return matchesTextQuery(searchQuery, [ticket.subject, ticket.ticket_number], {
      trim: false,
    });
  });

  const hasActiveFilters =
    statusFilter !== null || view === "all" || searchQuery.length > 0;

  const clearFilters = () => {
    setSearchQuery("");
    setSearchParams(new URLSearchParams());
  };

  return (
    <PageFrame
      title={t("list.title")}
      description={t("list.description")}
      backLink={{
        fallback: "/support",
        children: t("list.backToSupport"),
      }}
      actions={
        <Button
          onClick={() => navigate(NEW_SUPPORT_TICKET_PATH)}
          leftIcon={<Plus className="h-4 w-4" />}
        >
          {t("list.newTicket")}
        </Button>
      }
    >

      <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <TicketStatsGrid
          stats={stats}
          selectedStatus={statusFilter}
          selectedView={selectedView}
          onStatusSelect={updateStatus}
          onViewSelect={updateView}
        />
        <div className="flex items-center gap-2 xl:w-96">
          <SearchField
            searchLabel={tCommon("search")}
            clearSearchLabel={tCommon("clearSearch")}
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder={t("list.searchPlaceholder")}
            className="max-w-none min-w-0 flex-1"
          />
          <Button
            variant="outline"
            isLoading={isFetching}
            leftIcon={<RefreshCw className="h-4 w-4" />}
            onClick={() => void refetch()}
            className="shrink-0"
            aria-label={tCommon("refresh")}
          />
        </div>
      </div>

      <ListResultsCard
        title={t("adminList.ticketsFound", { count: filteredTickets.length })}
        isLoading={isLoading}
        loadingMessage={t("list.loadingTickets")}
        isError={isError}
        errorState={
          <ErrorState
            message={t("list.loadError")}
            onRetry={() => void refetch()}
            isRetrying={isFetching}
          />
        }
        isEmpty={!isLoading && !isError && filteredTickets.length === 0}
        emptyState={
          <EmptyState
            variant="dashed"
            icon={Ticket}
            title={t("list.noTicketsFound")}
            description={
              hasActiveFilters ? t("list.tryAdjusting") : t("list.createFirstHint")
            }
            action={
              hasActiveFilters ? (
                <Button variant="outline" onClick={clearFilters} size="sm">
                  {t("adminList.clearFilters")}
                </Button>
              ) : (
                <Button
                  onClick={() => navigate(NEW_SUPPORT_TICKET_PATH)}
                  leftIcon={<Plus className="h-4 w-4" />}
                >
                  {t("list.createTicket")}
                </Button>
              )
            }
          />
        }
        contentClassName="p-0"
      >
        <div className="divide-y divide-surface-200/70">
          {filteredTickets.map((ticket) => (
            <TicketListItem
              key={ticket.id}
              ticket={{
                id: ticket.id,
                ticket_number: ticket.ticket_number,
                subject: ticket.subject,
                status: ticket.status,
                priority: ticket.priority,
                category: ticket.category,
                owner_name: ticket.owner_name,
                message_count: ticket.message_count,
                created_at: ticket.created_at,
                last_message_at: ticket.last_message_at,
              }}
              to={`/support/tickets/${ticket.id}`}
              showPriority={showPriority}
              highlightAwaitingReply
            />
          ))}
        </div>
      </ListResultsCard>
    </PageFrame>
  );
}
